import {ko} from '../libs.js';
import '../../lib/ko.editables/ko.editables.js';
import {util, LOG} from '../common.js';

const STATUS = {
    LOADING: 1,
    LOADED: 2,
    SAVING: 3,
    SAVED: 4,
    EDITING: 5
};

function _getComputedStateFlag(obs, trueValue) {
    return ko.computed(() => obs() === trueValue);
}

/**
 * @constructor
 * @param props (Object) an object with observable properties that can be put in transactional state while editing
 *                       and rolled back on edit-cancel
 * @param [isReadOnly] (Boolean) optional flag to initialize the instance as readonly
 */
function EditableViewModelState(props, isReadOnly) {
    util.assertObject(props, 'Invalid/missing props object for EditableViewModelState');

    let transactionalProps = util.extend({}, props), // shallow copy
        _editStatus = ko.observable(),
        _loadStatus = ko.observable(),
        _saveStatus = ko.observable(),
        _errors = ko.observableArray(),
        _isReadOnly = ko.observable(!!isReadOnly),
        resetErrorsIfTrue = function(doReset) {
            if (doReset && _errors().length) {
                _errors.removeAll();
            }
        };

    // add transaction-related observables to the {transactionalProps} object; see also: `ko.editables.js`
    ko.editable(transactionalProps);

    this.errors = ko.computed({
        read: _errors,
        write: (singleErrorOrList) => {
            if (!singleErrorOrList && _errors().length) {
                _errors.removeAll();
            } else if (typeof singleErrorOrList === 'string') {
                _errors([singleErrorOrList]);
            } else if (Array.isArray(singleErrorOrList)) {
                _errors( singleErrorOrList.length ? singleErrorOrList : []);
            } else if (_errors().length) {
                LOG.warn('Unexpected value for singleErrorOrList: {}', singleErrorOrList);
                resetErrorsIfTrue(true);
            }
        }
    });
    this.isLoading = _getComputedStateFlag(_loadStatus, STATUS.LOADING);
    this.isLoaded = _getComputedStateFlag(_loadStatus, STATUS.LOADED);
    this.isSaving = _getComputedStateFlag(_saveStatus, STATUS.SAVING);
    this.isSaved = _getComputedStateFlag(_saveStatus, STATUS.SAVED);
    this.isEditing = _getComputedStateFlag(_editStatus, STATUS.EDITING);
    this.isReadOnly = ko.computed({
        read: _isReadOnly,
        write: (newIsReadOnly) => {
            util.assertBoolean(newIsReadOnly, 'Invalid value for newIsReadOnly param: {}', newIsReadOnly);
            if (newIsReadOnly) {
                util.assert(!this.isEditing.peek(), 'Tried to set isReadOnly(true) while in editing mode');
            }
            _isReadOnly(newIsReadOnly);
        }
    });

    this.isLoading.subscribe(resetErrorsIfTrue);
    this.isSaving.subscribe(resetErrorsIfTrue);

    // TODO maybe make this configurable via constructor opts parameter
    this.isEditing.subscribe(resetErrorsIfTrue);

    this.isBusy = ko.computed(() => this.isLoading() || this.isSaving());

    this.hasErrors = ko.computed(() => !!this.errors().length);

    this.startLoad = () => {
        util.assert(!this.isBusy(), 'Tried to start loading while busy');
        LOG.dev('Starting loading state..');
        _loadStatus(STATUS.LOADING);
        _saveStatus(null);
        return this;
    };

    this.endLoad = (success) => {
        util.assertBoolean(success, 'Missing success argument for EditableViewModelState.endLoad');
        util.assert(this.isLoading(), 'Tried to end load while not in loading state');
        LOG.dev('Ended loading state (success:%s)', success);
        _loadStatus(success ? STATUS.LOADED : null);
        _saveStatus(null);
        return this;
    };

    this.endLoadWithErrorCheck = () => this.endLoad(!this.errors().length);

    /**
     * Ends the `saving` status, committing the changes if no `errors` were set.
     * Also ends the `editing` status if no errors occurred.
     */
    this.endSaveAndEditIfNoErrors = () => {
        let hasErrors = this.hasErrors();
        this.endSave(!hasErrors);
        if (!hasErrors) {
            this.endEdit();
        }
    };

    this.canStartEdit = ko.computed(() =>
    this.isLoaded() &&
    !this.isEditing() &&
    !this.isBusy() &&
    !this.isReadOnly());

    this.startEdit = () => {
        util.assert(this.canStartEdit(), 'Cant start edit now');
        LOG.dev('Starting edit state..');
        transactionalProps.beginEdit();
        _editStatus(STATUS.EDITING);
        _saveStatus(null);
        LOG.dev('Edit state started');
        return this;
    };

    this.canEndEdit = ko.computed(() => this.isEditing() && !this.isBusy());

    this.endEdit = () => {
        util.assert(this.canEndEdit(), 'Cannot end edit now');
        LOG.dev('Ending edit state..');
        if (transactionalProps.inTransaction()) {
            LOG.dev('Rollback properties');
            transactionalProps.rollback();
        }
        _editStatus(null);
        LOG.dev('Edit state ended.');
        return this;
    };

    /**
     * Indicates whether it is (state-wise!) possible to start saving now.
     * (!) NO VALIDATION of values is included here (has to be done additionally by the caller).
     */
    this.canStartSave = ko.computed(() => !this.isBusy() && this.isLoaded());

    this.startSave = () => {
        util.assert(this.canStartSave, 'Tried to start save state while its not possible');
        LOG.dev('Starting save state..');
        _saveStatus(STATUS.SAVING);
        return this;
    };

    this.endSave = (success) => {
        util.assertBoolean(success, 'Missing success argument for EditableViewModelState.endSave');
        util.assert(this.isSaving(), 'Tried to end save while not in saving state');
        LOG.dev('Ending save state after %s..', success ? 'SUCCESS' : 'FAIL');
        if (success) {
            _saveStatus(STATUS.SAVED);
            if (transactionalProps.inTransaction()) {
                transactionalProps.commit();
                LOG.dev('Committed transactional props');
            }
        } else {
            _saveStatus(null);
        }
        LOG.dev('Save state ended');
        return this;
    };
}

export default EditableViewModelState;
